import React, { useMemo, useState } from 'react';

import { Autocomplete, Box, Chip, TextField, Tooltip } from '@mui/material';

import { sharedClasses } from './sharedClasses';
import { emailIsValid } from '../Utilities/emailIsValid';

import type { SyntheticEvent } from 'react';

export interface IRecipientUser {
  id: number;
  email: string;
  full_name: string;
}

type Recipient = IRecipientUser | string;

const getRecipientEmail = (recipient: Recipient): string =>
  typeof recipient === 'string' ? recipient : recipient.email;

const isSameRecipient = (a: Recipient, b: Recipient): boolean =>
  getRecipientEmail(a).toLowerCase() === getRecipientEmail(b).toLowerCase();

const RecipientInput = ({
  label,
  recipients,
  setRecipients,
  userOptions,
  loadingOptions,
  error,
  helperText,
  required,
  disabled,
  maxRecipients,
  onRecipientsChange,
  disablePortal = false
}: {
  label: string;
  recipients: string[];
  setRecipients: (recipients: string[]) => void;
  userOptions: IRecipientUser[];
  loadingOptions?: boolean;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  maxRecipients?: number;
  onRecipientsChange?: (recipients: string[]) => void;
  disablePortal?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [highlightedOption, setHighlightedOption] = useState<IRecipientUser | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Reconstruct each committed email as its matching IRecipientUser (for tooltip/display)
  // whenever one exists, so the parent only ever has to store plain email strings.
  const value = useMemo<Recipient[]>(
    () =>
      recipients.map(
        (email) =>
          userOptions.find((option) => option.email.toLowerCase() === email.toLowerCase()) ?? email
      ),
    [recipients, userOptions]
  );

  const capReached = !!maxRecipients && recipients.length >= maxRecipients;
  const capMessage = maxRecipients ? `Maximum ${maxRecipients} recipients reached` : '';
  // Only disable the text input when the cap is reached — disabling the whole
  // Autocomplete root would set pointer-events: none, which would also block
  // deleting existing chips to get back under the cap.
  const isInputDisabled = !!disabled || capReached;
  const isDropdownOpen = isOpen && !capReached;
  const displayedError =
    [error, validationMessage, capReached ? capMessage : ''].find((message) => Boolean(message)) ??
    '';

  const combinedHelperText =
    displayedError || helperText ? (
      <>
        {!!displayedError && (
          <Box component="span" sx={{ color: '#CF726F', display: 'block' }}>
            {displayedError}
          </Box>
        )}
        {!!helperText && (
          <Box component="span" sx={{ color: '#CCCCCC', display: 'block' }}>
            {helperText}
          </Box>
        )}
      </>
    ) : undefined;

  const updateRecipients = (nextRecipients: string[]) => {
    setRecipients(nextRecipients);
    onRecipientsChange?.(nextRecipients);
  };

  const commitRecipient = (candidate: Recipient) => {
    if (capReached) {
      setValidationMessage(capMessage);
      return;
    }
    if (value.some((existing) => isSameRecipient(existing, candidate))) {
      setValidationMessage('Recipient already added');
      return;
    }
    if (typeof candidate === 'string' && !emailIsValid(candidate)) {
      setValidationMessage('Must be a valid email address');
      return;
    }
    setValidationMessage('');
    updateRecipients([...recipients, getRecipientEmail(candidate)]);
    setInputValue('');
  };

  // Mirrors MultiTagInput's paste behaviour: a pasted list of comma/whitespace
  // separated emails is split into individual tokens and committed one by one,
  // instead of being treated as a single (invalid) string.
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = event.clipboardData.getData('text');
    const tokens = pasted
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter(Boolean);
    if (tokens.length <= 1) return;

    event.preventDefault();

    const seen = new Set(recipients.map((email) => email.toLowerCase()));
    const invalidTokens: string[] = [];
    const nextRecipients = [...recipients];
    let addedCount = 0;

    tokens.forEach((token) => {
      if (maxRecipients && nextRecipients.length >= maxRecipients) return;
      const lowerToken = token.toLowerCase();
      if (seen.has(lowerToken)) return;
      const matchedUser = userOptions.find((option) => option.email.toLowerCase() === lowerToken);
      if (!matchedUser && !emailIsValid(token)) {
        invalidTokens.push(token);
        return;
      }
      seen.add(lowerToken);
      addedCount += 1;
      nextRecipients.push(matchedUser ? matchedUser.email : token);
    });

    if (addedCount > 0) {
      updateRecipients(nextRecipients);
    }

    setInputValue('');
    if (invalidTokens.length > 0) {
      setValidationMessage(
        `Invalid email address${invalidTokens.length > 1 ? 'es' : ''}: ${invalidTokens.join(', ')}`
      );
    } else if (maxRecipients && recipients.length + addedCount >= maxRecipients) {
      setValidationMessage(capMessage);
    } else {
      setValidationMessage('');
    }
  };

  const filterOptions = (options: IRecipientUser[], query: string) => {
    const alreadySelected = new Set(recipients.map((email) => email.toLowerCase()));
    const normalizedQuery = query.trim().toLowerCase();
    return options.filter((option) => {
      if (alreadySelected.has(option.email.toLowerCase())) return false;
      if (!normalizedQuery) return true;
      return (
        option.full_name.toLowerCase().includes(normalizedQuery) ||
        option.email.toLowerCase().includes(normalizedQuery)
      );
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const trimmedValue = inputValue.trim();

    // Space only commits when the input is already a complete, valid email —
    // otherwise it's ordinary whitespace within a name search and must pass through.
    if (event.key === ' ' && !emailIsValid(trimmedValue)) return;
    if (event.key !== 'Enter' && event.key !== ',' && event.key !== ' ') return;
    if (!trimmedValue) return;

    // If the user has arrow-key-highlighted a dropdown option, defer to MUI's
    // own Enter handling (via `onChange`) instead of racing it with a manual
    // commit here — only step in ourselves when nothing is highlighted.
    if (event.key === 'Enter' && highlightedOption) return;

    event.preventDefault();

    const matches = filterOptions(userOptions, inputValue);
    if (matches.length === 1) {
      commitRecipient(matches[0]);
      return;
    }

    commitRecipient(trimmedValue);
  };

  return (
    <Autocomplete
      disableClearable
      filterSelectedOptions
      freeSolo
      multiple
      disabled={!!disabled}
      disablePortal={disablePortal}
      filterOptions={(options, state) => filterOptions(options, state.inputValue)}
      forcePopupIcon={!capReached}
      getOptionLabel={getRecipientEmail}
      inputValue={inputValue}
      isOptionEqualToValue={(option, optionValue) => isSameRecipient(option, optionValue)}
      loading={loadingOptions}
      onClose={() => setIsOpen(false)}
      onHighlightChange={(_event, option) => setHighlightedOption(option)}
      onOpen={() => setIsOpen(true)}
      open={isDropdownOpen}
      options={userOptions}
      slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
      sx={sharedClasses.formAutocomplete}
      value={value}
      onBlur={() => {
        if (inputValue.trim()) commitRecipient(inputValue.trim());
      }}
      onChange={(_event: SyntheticEvent, newValue: Recipient[], reason) => {
        if (reason === 'removeOption' || reason === 'clear') {
          updateRecipients(newValue.map(getRecipientEmail));
          setValidationMessage('');
          return;
        }
        const added = newValue[newValue.length - 1];
        if (added !== undefined) {
          commitRecipient(added);
        }
      }}
      onInputChange={(_event, inputText, reason) => {
        if (reason === 'input') setInputValue(inputText);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          disabled={isInputDisabled}
          error={!!displayedError}
          helperText={combinedHelperText}
          label={label}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          required={required}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: {
              ...params.inputProps,
              disabled: isInputDisabled,
              'data-testid': 'multiple-tags-input'
            }
          }}
          sx={{
            ...sharedClasses.autoCompleteTextfield,
            '& .MuiFormHelperText-root': { color: 'unset', '&.Mui-error': { color: 'unset' } }
          }}
        />
      )}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={option.id}>
          {option.full_name} &lt;{option.email}&gt;
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((recipient, index) => {
          const tagProps = getTagProps({ index });
          const chip = (
            <Chip
              {...tagProps}
              key={getRecipientEmail(recipient)}
              label={getRecipientEmail(recipient)}
            />
          );
          return typeof recipient === 'string' ? (
            chip
          ) : (
            <Tooltip key={recipient.id} followCursor title={recipient.full_name}>
              {chip}
            </Tooltip>
          );
        })
      }
    />
  );
};

export default RecipientInput;
